import {addresses, legalLinks, socialLinks} from "~/components/_data";
import {useFrontendProxyUrl} from "~/root/rootLoaderData";

/* === AddressBlock, direct port of components/atoms/footerAddressBlock.tsx === */
function AddressBlock({Src, Alt, location, address, Ph}: {Src: string; Alt: string; location: string; address: string; Ph: string}) {
    return (
        <div className="grid gap-2">
            <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                <img
                    src={Src}
                    alt={Alt}
                    className="w-8 h-8"
                    loading="lazy"
                />
                <span className="text-nav-text-b">{location}</span>
            </div>
            <div className="text-nav-text font-light">{address}</div>
            <div className="text-nav-text font-light">{Ph}</div>
        </div>
    );
}

/* === SocialLink, direct port of components/atoms/footerSocialLink.tsx ===
 * Hover filter (the magic filter chain) turns the icon into the primary brand color
 */
function SocialLink({href, src, alt}: {href: string; src: string; alt: string}) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener"
            className="no-underline"
        >
            <img
                src={src}
                alt={alt}
                className="h-[34px] w-[34px] p-1 transition-[filter] duration-[250ms] hover:[filter:brightness(0)_saturate(100%)_invert(34%)_sepia(97%)_saturate(2551%)_hue-rotate(223deg)_brightness(95%)_contrast(101%)]"
            />
        </a>
    );
}

/* === Gradient, direct port of components/atoms/footerGradientStroke.tsx === */
function Gradient() {
    return <div className="top-0 left-0 w-[50%] h-[1px] bg-gradient-to-r from-[#00A2ED] via-[#1F40CB] to-transparent" />;
}

/* === Footer, slimmed for Intellsys OS.
 * Removed: products + static nav, big GJ wordmark, Industries + Capabilities lists.
 * Kept: AdGPT logo banner, address grid, bottom row (legal / social / copyright).
 */
export function Footer() {
    const signInUrl = useFrontendProxyUrl("/sign-in");

    return (
        <footer className="w-full h-full grid grid-cols-1 gap-3 md:px-lg2 md:gap-lg md:pt-lg2 p-lg bg-surface-primary-background">
            {/* AdGPT logo banner + address grid */}
            <div className="grid gap-lg">
                <div className="w-full grid place-items-center overflow-hidden">
                    <a
                        href={signInUrl}
                        className="relative grid items-center py-md3 px-lg group no-underline"
                    >
                        <img
                            alt="AdGpt"
                            loading="lazy"
                            src="https://growthjockey-prod.b-cdn.net/new/AdGptLogoWhite.webp"
                            className="col-start-1 row-start-1"
                        />
                        <div className="absolute bg-surface-primary-background grid -right-[80rem] h-[12rem] w-[80rem] duration-[1500ms] group-hover:-translate-x-[66rem]">
                            <img
                                alt="Try Adgpt now"
                                loading="lazy"
                                src="https://growthjockey-prod.b-cdn.net/new/adgpt-try-now.webp"
                                className="ml-lg col-start-1 row-start-1"
                            />
                        </div>
                    </a>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
                    {addresses.map((address, i) => (
                        <AddressBlock
                            key={i}
                            Src={address.flagSrc}
                            Alt={address.flagAlt}
                            location={address.location}
                            address={address.address}
                            Ph={address.phone}
                        />
                    ))}
                </div>
            </div>

            {/* Bottom, gradient stroke, then legal / social / copyright */}
            <div className="w-full relative">
                <Gradient />
                <div className="w-full px-4 md:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-sm2 py-lg">
                        <div className="text-nav-text-b grid grid-flow-col auto-cols-max justify-center gap-sm2">
                            {legalLinks.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    className="block no-underline text-text-primary hover:text-primary-500 transition-colors duration-[250ms]"
                                >
                                    {link.text}
                                </a>
                            ))}
                        </div>
                        <div className="grid grid-flow-col justify-center gap-x-6">
                            {socialLinks.map((link, i) => (
                                <SocialLink
                                    key={i}
                                    href={link.href}
                                    src={link.src}
                                    alt={link.alt}
                                />
                            ))}
                        </div>
                        <div className="text-nav-text-b text-center md:text-right">
                            &copy;{new Date().getFullYear()} GrowthJockey Pvt. Ltd.
                            <br />
                            <span> All Rights Reserved.</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
